/**
 * OmniLeagues — AI Helpdesk Widget
 *
 * Floating bubble (bottom-right). AI chat through the OL helpdesk backend:
 * ten languages with auto-translate, unread badge, escalation to a person,
 * CSAT, and file attachments.
 *
 * Styled to match BackOfficePro's widget (AJ, 2026-10-16: "I want OL Helpdesk
 * to look exactly like BOP....not close") — a white panel under a violet
 * gradient header, bubbles labelled with who said it and when, and a round
 * send button. The behaviour underneath is OL's own and was already working;
 * only the presentation changed, plus attachments which OL did not have.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  MessageCircle, Send, X, Loader2, Globe, UserCheck, Star, CheckCircle2,
  Sparkles, ImagePlus, Paperclip, Plus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' }, { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' }, { code: 'pt', label: 'Português' },
  { code: 'zh', label: '中文' }, { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' }, { code: 'ar', label: 'العربية' },
];
const LANG_KEY = 'ol.helpdesk.lang';
const UNREAD_KEY = 'ol.helpdesk.unread';

export default function HelpdeskWidget() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || 'en');
  const [conversation, setConversation] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(() => parseInt(localStorage.getItem(UNREAD_KEY) || '0', 10));
  const [csatMode, setCsatMode] = useState(false);
  const [csatRating, setCsatRating] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { localStorage.setItem(LANG_KEY, lang); }, [lang]);
  useEffect(() => { localStorage.setItem(UNREAD_KEY, String(unread)); }, [unread]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/helpdesk/conversations/my`);
      const latest = (data.conversations || []).find(c => c.status !== 'resolved') || null;
      if (latest) setConversation(latest);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, 80);
    }
  }, [open, conversation]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/helpdesk/chat`, {
        message: text,
        conversation_id: conversation?.id,
        language: lang,
      });
      setConversation(data.conversation);
      if (!open) setUnread(u => u + 1);
    } catch (e) {
      // Surface error as an inline assistant message so the user sees it.
      setConversation(prev => prev ? {
        ...prev,
        messages: [...(prev.messages || []), {
          id: String(Math.random()),
          role: 'assistant',
          content: e.response?.data?.detail || 'Sorry, I had trouble reaching the AI. Please try again.',
          timestamp: new Date().toISOString(),
          is_human: false,
        }],
      } : prev);
    } finally {
      setSending(false);
    }
  };

  /** Attach files to the current conversation.
   *  Needs a conversation to attach TO, so if the user picks a file before
   *  saying anything we ask for a message first rather than silently dropping
   *  the file. */
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!conversation?.id) {
      setAttachments([]);
      alert('Send a message first, then attach files to the conversation.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      const { data } = await axios.post(
        `${API_URL}/api/helpdesk/conversations/${conversation.id}/upload-files`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments((prev) => [...prev, ...(data.attachments || [])]);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not attach that file.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /** Paste a screenshot straight into the composer, the way BOP's does. */
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter(i => i.kind === 'file').map(i => i.getAsFile()).filter(Boolean);
    if (files.length) { e.preventDefault(); upload(files); }
  };

  const escalate = async () => {
    if (!conversation) return;
    try {
      await axios.post(`${API_URL}/api/helpdesk/conversations/${conversation.id}/escalate`, { reason: 'User requested human agent' });
      setConversation(prev => prev ? { ...prev, status: 'escalated' } : prev);
    } catch { /* ignore */ }
  };

  const submitCsat = async () => {
    if (!conversation || csatRating === 0) return;
    try {
      await axios.post(`${API_URL}/api/helpdesk/conversations/${conversation.id}/csat`, { rating: csatRating });
      setCsatMode(false);
      setCsatRating(0);
    } catch { /* ignore */ }
  };

  const startFresh = () => {
    setConversation(null);
    setCsatMode(false);
    setCsatRating(0);
  };

  const messages = conversation?.messages || [];
  const isResolved = conversation?.status === 'resolved';
  const canOfferCsat = isResolved && conversation?.csat_rating == null;

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        title="OmniLeagues Support"
        data-testid="helpdesk-launcher"
      >
        <MessageCircle className="h-6 w-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="helpdesk-unread">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-h-[620px] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-zinc-200 bg-white" data-testid="helpdesk-panel">
          {/* Header — BOP's violet gradient with the assistant mark */}
          <div className="flex items-center justify-between px-3 py-3 text-white bg-gradient-to-r from-indigo-600 to-violet-600">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold leading-tight truncate">OL Support</div>
                <div className="text-[11px] opacity-80 leading-tight truncate">
                  {conversation?.status === 'escalated' ? 'A person is joining' :
                   conversation?.mode === 'human' ? 'Chatting with a person' :
                   'AI-powered assistant'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className="flex items-center gap-0.5 rounded px-1 hover:bg-white/10">
                <Globe className="h-3.5 w-3.5 opacity-80" />
                <select value={lang} onChange={e => setLang(e.target.value)}
                  className="bg-transparent text-white text-[11px] uppercase border-0 focus:outline-none cursor-pointer"
                  data-testid="helpdesk-lang">
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code} className="text-slate-900">{l.code.toUpperCase()}</option>
                  ))}
                </select>
              </div>
              <button onClick={startFresh} className="p-1 hover:bg-white/10 rounded"
                title="Start a new conversation" data-testid="helpdesk-new-chat">
                <Plus className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="helpdesk-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-white" style={{ maxHeight: '420px' }}>
            {messages.length === 0 && (
              <div className="text-center text-zinc-500 text-xs py-8 px-4">
                <Sparkles className="h-6 w-6 mx-auto mb-2 opacity-50" />
                Ask me anything about OmniLeagues — schedules, rosters, publishing to your
                website, registration, finance. I speak 10 languages, and I can hand you to a
                person whenever you want.
              </div>
            )}
            {messages.map(m => (
              <MsgBubble key={m.id} msg={m} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-zinc-500 pl-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
              </div>
            )}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-end">
                {attachments.map(a => (
                  <span key={a.id} data-testid={`attachment-${a.id}`}
                    className="inline-flex items-center gap-1 text-[10px] bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">
                    <Paperclip className="h-3 w-3" />{a.filename}
                  </span>
                ))}
              </div>
            )}

            {canOfferCsat && !csatMode && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-xs text-zinc-600 space-y-2">
                <div className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /> Conversation resolved.</div>
                <button onClick={() => setCsatMode(true)} className="text-indigo-600 hover:text-indigo-700 font-semibold" data-testid="helpdesk-rate">Rate this chat →</button>
              </div>
            )}
            {csatMode && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-2" data-testid="helpdesk-csat">
                <div className="text-xs text-zinc-600">How did we do?</div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setCsatRating(n)} className={`h-8 w-8 rounded-full flex items-center justify-center ${csatRating >= n ? 'text-amber-500' : 'text-zinc-300'}`}>
                      <Star className="h-5 w-5" fill={csatRating >= n ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
                <button onClick={submitCsat} disabled={csatRating === 0} className="text-xs text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1 rounded font-semibold disabled:opacity-50">Submit</button>
              </div>
            )}
          </div>

          {/* Footer controls */}
          {!isResolved && (
            <div className="border-t border-zinc-200 bg-white p-2 space-y-2">
              {conversation && conversation.status !== 'escalated' && conversation.mode !== 'human' && (
                <button onClick={escalate} className="w-full text-[11px] text-zinc-500 hover:text-indigo-600 flex items-center justify-center gap-1 py-1" data-testid="helpdesk-escalate">
                  <UserCheck className="h-3 w-3" /> Talk to a person
                </button>
              )}
              <div className="flex items-center gap-1">
                <input ref={fileRef} type="file" multiple className="hidden"
                  onChange={(e) => upload(e.target.files)} data-testid="helpdesk-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-9 w-9 rounded-full text-zinc-400 hover:text-indigo-600 hover:bg-zinc-100 flex items-center justify-center shrink-0 disabled:opacity-40"
                  title="Attach a file" data-testid="helpdesk-attach">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                </button>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onPaste={onPaste}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type your message or paste a screenshot"
                  className="flex-1 min-w-0 bg-white border border-zinc-200 text-zinc-900 text-sm rounded-full px-3 py-2 focus:outline-none focus:border-indigo-400"
                  data-testid="helpdesk-input"
                />
                <button onClick={send} disabled={sending || !input.trim()}
                  className="h-9 w-9 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center shrink-0 disabled:opacity-40"
                  data-testid="helpdesk-send">
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {isResolved && (
            <div className="border-t border-zinc-200 bg-white p-3 text-center">
              <button onClick={startFresh} className="text-xs text-indigo-600 font-semibold hover:underline" data-testid="helpdesk-new">
                Start a new conversation
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** A message, labelled with who said it and when — BOP puts that above each
 *  bubble rather than inside it, which reads better when a conversation moves
 *  between the assistant and a person. */
function MsgBubble({ msg }) {
  const isUser = msg.role === 'user';
  const isHuman = !!msg.is_human;
  const who = isUser ? 'You' : (isHuman ? (msg.author_name || 'OL Support') : 'OL Support');
  let when = '';
  try {
    when = msg.timestamp
      ? new Date(msg.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : '';
  } catch { when = ''; }

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
      <div className="flex items-center gap-1.5 mb-0.5 px-1">
        {!isUser && (
          <span className={`h-4 w-4 rounded flex items-center justify-center ${
            isHuman ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>
            {isHuman ? <UserCheck className="h-2.5 w-2.5" /> : <Sparkles className="h-2.5 w-2.5" />}
          </span>
        )}
        <span className="text-[10px] text-zinc-400">{who}{when ? ` · ${when}` : ''}</span>
      </div>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs whitespace-pre-wrap leading-snug ${
        isUser
          ? 'bg-indigo-600 text-white rounded-br-sm'
          : isHuman
            ? 'bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-bl-sm'
            : 'bg-zinc-100 text-zinc-800 rounded-bl-sm'
      }`}>
        {msg.content}
      </div>
    </div>
  );
}
